import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useNavigate, useParams } from 'react-router-dom'
import {PageLink, PageTitle} from '../../../../../_metronic/layout/core/PageData'
import {FormikProvider, useFormik} from 'formik'
import { preventEnterSubmit } from '../../../../util/Functions'
import {CustomInputRenderer} from '../../../../common/component/CustomInputRenderer'
import { array, object, string } from 'yup'
import 'flatpickr/dist/themes/light.css'
import { SubSectionHeader } from '../../../../common/component/SubSectionHeader'
import { FilePicker } from '../../../../common/component/FilePicker'
import { IAttachment } from '../../../media/Media.model'
import { MessageToast, MessageType } from '../../../../common/component/MessageToast'
import { Toast } from 'bootstrap'
import { IContactDto } from '../../../contact/Contact.model'
import FormValidator from '../../../../util/FormValidator.util'
import { useTaskCreateMutation } from '../Task.api'
import { TicketEmbedered } from '../../ticket/components/TicketEmbedered'
import { ContactListItem } from '../../../contact/components/ContactListItem'
import { ContactPicker } from '../../../contact/components/ContactPicker'
import { ITaskCreateRequestDto, TaskStatus, TaskType } from '../Task.model'

interface ICreateFormikSchema {
  ticketId: string
  statusCode: string
  status: string
  typeCode: string
  type: string
  title: string
  assignedTo?: string
  description?: string
  attachments: IAttachment[]
}

export const TaskNewPage = () => {
  const intl = useIntl()
  const formValidator = new FormValidator(intl)
  const navigate = useNavigate()
  const {ticketId} = useParams()
  const breadCrumbs: Array<PageLink> = [
    {
      title: intl.formatMessage({id: 'BREADCRUMBS.TICKETS'}),
      path: '/tickets',
      isSeparator: false,
      isActive: false,
    },
    {
      title: `-`,
      path: '',
      isSeparator: true,
      isActive: false,
    },
    {
      title: `${ticketId}`,
      path: `/tickets/${ticketId}`,
      isSeparator: false,
      isActive: false,
    },
  ]

  const [loadError, setLoadError] = useState<string | undefined>(undefined)
  const [toastEntry, setToastEntry] = useState<[MessageType, string]>()
  const [validationSchema, setValidationSchema] = useState(
    object().shape({
      ticketId: formValidator.validateStringStrict(),
      typeCode: formValidator.validateStringStrict(),
      type: string().when('typeCode', {
        is: (value) => value === 'OTHER',
        then: () => formValidator.validateStringStrict(),
      }),
      title: formValidator.validateStringStrict(),
      assignedTo: formValidator.validateString(),
      description: formValidator.validateTextArea(),
      attachments: array().of(
        object({
          comments: formValidator.validateString(),
        })
      ),
    })
  )
  const [initialValues, setInitialValues] = useState<ICreateFormikSchema>({
    ticketId: ticketId || '',
    typeCode: TaskType.INCIDENT,
    type: intl.formatMessage({id: `DROPDOWN.TASK_TYPE.${TaskType.INCIDENT}`}),
    statusCode: TaskStatus.NEW,
    status: intl.formatMessage({id: `DROPDOWN.TASK.STATUS.${TaskStatus.NEW}`}),
    title: '',
    description: undefined,
    assignedTo: undefined,
    attachments: [],
  })

  const [contact, setContact] = useState<IContactDto | undefined>(undefined) 
  const [isAttachmentProcessing, setIsAttachmentProcessing] = useState(false)
  const [taskCreate] = useTaskCreateMutation();
  
  const formik = useFormik<ICreateFormikSchema>({
    enableReinitialize: true,
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      try {
        if (values) {
          setSubmitting(true)

          const request: ITaskCreateRequestDto = {
            ticketId: values.ticketId,
            statusCode: values.statusCode,
            status: values.status,
            typeCode: values.typeCode,
            type:
              values.typeCode === TaskType.OTHER
                ? values.typeCode.trim().toLocaleUpperCase()
                : intl.formatMessage({id: `DROPDOWN.TASK_TYPE.${values.typeCode}`}),
            title: values.title,
            description: values.description,
            assignedTo: values.assignedTo,
            attachments: values.attachments,
          }

          const response: any = await taskCreate(request).unwrap()
          if (response && response.error) {
            setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
          } else {
            navigate(`/tickets/${ticketId}/tasks/${response.task.id}`)
          }
        }
      } catch (e: any) {
        setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
      } finally {
        setSubmitting(false)
      }
    },
  })

  useEffect(() => {
    if (toastEntry) {
      const [messageType] = toastEntry
      const toastElement = document.getElementById('#CustomToast')
      const delay =
        messageType === MessageType.ERROR || messageType === MessageType.WARNING ? 10000 : 2000
      if (toastElement) {
        toastElement.addEventListener('hidden.bs.toast', () => setToastEntry(undefined))
        const toast = new Toast(toastElement, {autohide: true, delay})
        toast.show()
      }
    }
  }, [toastEntry])

  const contactSelectOnClickHandler = async (contact) => {
    if (contact) {
      setContact(contact)
      formik.setFieldValue('assignedTo', contact.id)
    }
  }

  const contactRemoveOnClickHandler = async () => {
    setContact(undefined)
    formik.setFieldValue('assignedTo', '')
  }

  const tabWithError = (tabId) => {
    switch (tabId) {
      case 'kt_tab_pane_1': {
        return (
          (formik.touched.ticketId && formik.errors.ticketId) ||
          (formik.touched.typeCode && formik.errors.typeCode) ||
          (formik.touched.type && formik.errors.type) ||
          (formik.touched.title && formik.errors.title)
        )
      }
      default:
        return false
    }
  }

  return (
    <>
      {toastEntry && <MessageToast messageType={toastEntry[0]} message={toastEntry[1]} />}
      <PageTitle breadcrumbs={breadCrumbs}></PageTitle>
      <div className='card card-flush card-stretch-100 card-bordered mt-5'>
        <div className='card-body'>
          {loadError && (
            <div className='alert alert-danger d-flex align-items-center px-5 py-2 mb-10'>
              <span className='svg-icon svg-icon-2hx svg-icon-primary ms-3 me-5'>
                <i className='bi bi-database-exclamation fs-2'></i>
              </span>

              <div className='d-flex flex-column'>
                <span>{loadError}</span>
              </div>

              <button
                type='button'
                className='position-absolute position-sm-relative m-2 m-sm-0 top-0 end-0 btn btn-icon ms-sm-auto'
                data-bs-dismiss='alert'
              >
                <div className='badge px-3 py-2'>
                  <span className='text-danger fs-1'>&times;</span>
                </div>
              </button>
            </div>
          )}

          <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit} onKeyDown={preventEnterSubmit} noValidate>
              <div className='d-flex flex-row'>
                <div className='d-flex flex-column flex-row-fluid'>
                  <div className='d-flex flex-row flex-column-fluid'>
                    <div className='d-flex flex-row-fluid flex-start'>
                      <div className='d-flex flex-column'>
                        <span className='text-gray-800 fs-2 fw-bolder'>
                          {intl.formatMessage({id: 'UTIL.CREATE.TASK'})}
                        </span>
                      </div>
                    </div>

                    <div className='d-flex flex-row-auto flex-start ms-5'>
                      <button
                        type='submit'
                        className='btn btn-sm btn-primary me-2 d-none d-md-block'
                        onClick={() => formik.handleSubmit()}
                        disabled={isAttachmentProcessing || formik.isSubmitting}
                      >
                        {formik.isSubmitting ? (
                          <span className='indicator-progress' style={{display: 'block'}}>
                            {intl.formatMessage({id: 'UTIL.PROCESSING'})}
                            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                          </span>
                        ) : (
                          <>
                            <i className='bi bi-floppy'></i>
                            <span className='indicator-label'>
                              {intl.formatMessage({id: 'UTIL.SAVE'})}
                            </span>
                          </>
                        )}
                      </button>
                      <button
                        type='submit'
                        className='btn btn-sm btn-primary btn-active-color-secondary me-2 d-md-none'
                        onClick={() => formik.handleSubmit()}
                        disabled={isAttachmentProcessing || formik.isSubmitting}
                      >
                        {formik.isSubmitting ? (
                          <span className='indicator-progress' style={{display: 'block'}}>
                            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                          </span>
                        ) : (
                          <i className='bi bi-floppy'></i>
                        )}
                      </button>

                      {!(isAttachmentProcessing || formik.isSubmitting) && (
                        <button
                          className='btn btn-sm btn-light'
                          onClick={() => navigate(`/tickets/${ticketId}`)}
                        >
                          <i className='bi bi-x-lg'></i>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <ul className='nav nav-tabs nav-line-tabs mb-5 fs-6 mt-5'>
                  <li className='nav-item'>
                    <a className='nav-link active' data-bs-toggle='tab' href='#kt_tab_pane_1'>
                      {tabWithError('kt_tab_pane_1') && <span className='text-danger me-1'>*</span>}
                      {intl.formatMessage({id: 'UTIL.INFORMATION'})}
                    </a>
                  </li>
                </ul>
                <div className='tab-content mt-10' id='myTabContent'>
                  <div className='tab-pane fade active show' id='kt_tab_pane_1' role='tabpanel'>
                    {ticketId && (
                      <div className='row mb-5'>
                        <div className='px-5'>
                          <TicketEmbedered ticketId={ticketId} />
                        </div>
                      </div>
                    )}

                    <div className='row mb-5'>
                      <SubSectionHeader
                        separator={true}
                        title={intl.formatMessage({id: 'UTIL.GENERAL'})}
                      />

                      <div className='px-5'>
                        <div className='row mb-5'>
                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'TICKET.TITLE'})}
                            required={true}
                            placeholder={intl.formatMessage({id: 'TICKET.TITLE'})}
                            disabled={formik.isSubmitting}
                            formik={formik}
                            fieldName={`title`}
                            enableInputLength={true}
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'TICKET.DESCRIPTION'})}
                            disabled={formik.isSubmitting}
                            formik={formik}
                            fieldName='description'
                            object={
                              <textarea
                                rows={4}
                                className='form-control form-control-lg form-control-solid px-5 py-3 fs-6 text-dark'
                                placeholder={intl.formatMessage({id: 'TICKET.DESCRIPTION'})}
                                {...formik.getFieldProps('description')}
                                onChange={(e) => formik.handleChange(e)}
                                disabled={formik.isSubmitting}
                              />
                            }
                            enableInputLength={true}
                            inputLength={3000}
                          ></CustomInputRenderer>

                          {!contact && (
                            <div className='row my-5'>
                              <ContactPicker
                                label={intl.formatMessage({id: 'UTIL.ASSIGNED_TO'})}
                                required={false}
                                enabled={!formik.isSubmitting}
                                selectOnClick={contactSelectOnClickHandler}
                              ></ContactPicker>
                            </div>
                          )}

                          {contact && (
                            <div className='row my-5'>
                              <label className='d-flex flex-column-auto flex-start align-items-start col-xxl-3 fs-6 col-form-label col-form-label-sm'>
                                <span className='text-gray-700'>
                                  {intl.formatMessage({id: 'UTIL.ASSIGNED_TO'})}
                                </span>
                              </label>

                              <div className='col-xxl-9'>
                                <ContactListItem
                                  disabled={formik.isSubmitting}
                                  data={contact}
                                  rowSelected={contact.id}
                                  removeOnClick={contactRemoveOnClickHandler}
                                ></ContactListItem>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className='row mb-5'>
                      <SubSectionHeader title={intl.formatMessage({id: 'UTIL.ATTACHMENTS'})} />

                      <div className='px-5'>
                        <div className='row mb-5'>
                          <FilePicker
                            disabled={formik.isSubmitting}
                            formik={formik}
                            fieldName='attachments'
                            setIsAttachmentProcessing={setIsAttachmentProcessing}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </FormikProvider>
        </div>
      </div>
    </>
  )
}
